/**
 * Module-home Tile + TileSection primitives (P33.1).
 *
 * Lifted from TwinHome (P32.2) so Foundation + Framework + any
 * future module home shares the same tile shape. Adds a brass
 * underline accent (P33's v1 lift): a 3px brass strip at the
 * bottom of each card, matching the `boxShadow: "inset 0 -3px 0
 * #C8A24A"` pattern v1 used across HomeView + GettingStartedView.
 *
 * Why inline style for the underline: the brass colour (#C8A24A)
 * isn't in Tailwind's theme as a util. Promoting to a theme
 * extension is a separate change (rejected for v0.33.0 to
 * minimise yak-shave).
 *
 * Module-boundary rule (eslint): lives in shared/ so any module
 * can import it.
 */
import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { MarsadIcon } from "shared/components/MarsadIcon";
import type { MarkName } from "shared/icons/marks.generated";

const BRASS_UNDERLINE = { boxShadow: "inset 0 -3px 0 #C8A24A" } as const;

interface TileProps {
  to: string;
  name: string;
  desc: string;
  testid: string;
  /** Hide tile entirely when false (e.g. admin-gating). */
  visible?: boolean;
  /** P283.3: the tile's Marsad Mark, brass-tinted above the name. */
  icon?: MarkName;
  /**
   * Round 10 A3 -- who may open what is behind this tile.
   *
   * A shelf of tiles that all LOOK openable, three of which refuse the
   * reader when pressed, is the repeat defect this programme spent four
   * rounds closing. Where a destination is seat-gated, the tile says
   * whose it is rather than leaving the reader to find out by being
   * turned away.
   */
  who?: string;
}

export function Tile({ to, name, desc, testid, visible = true, icon, who }: TileProps) {
  if (!visible) return null;
  return (
    <Link
      to={to}
      data-testid={testid}
      style={BRASS_UNDERLINE}
      className={[
        "block rounded-lg border border-slate-200 bg-white p-4",
        "shadow-sm hover:border-brand-brass hover:shadow-md hover:ring-1 hover:ring-brand-brass/40",
        "transition-all",
      ].join(" ")}
    >
      {icon && (
        <MarsadIcon name={icon} size={20} className="mb-2 text-brand-brassDark" />
      )}
      <div className="text-sm font-semibold text-slate-900">{name}</div>
      <div className="mt-1 text-xs text-slate-600">{desc}</div>
      {who && (
        <div data-testid={`${testid}-who`} className="mt-1 text-[11px] text-slate-500">
          {who}
        </div>
      )}
    </Link>
  );
}

interface TileSectionProps {
  heading: string;
  testid: string;
  children: ReactNode;
}

export function TileSection({ heading, testid, children }: TileSectionProps) {
  return (
    <section data-testid={testid} className="space-y-2">
      <h2 className="text-[10px] font-bold uppercase tracking-widest text-slate-500">
        {heading}
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {children}
      </div>
    </section>
  );
}

/**
 * ModuleRubric -- v1 lift (P33). Small-caps, wide letter spacing,
 * slate-500. Renders above the module name to anchor the page
 * in a "this is a MARSAD framing surface" rhythm. v1 used the
 * pattern via `text-[10px] font-semibold uppercase tracking-[3px]
 * text-slate-500` on HomeView + GettingStartedView and we lifted
 * the same shape into BIAN classic ("Architects -- Our Stories")
 * in P30.
 */
export function ModuleRubric({ children }: { children: ReactNode }) {
  return (
    <p
      data-testid="module-rubric"
      className="text-[10px] font-bold uppercase tracking-[3px] text-slate-500"
    >
      {children}
    </p>
  );
}
